
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { RichTextEditor } from "@/components/ui/rich-text-editor";

interface CommunityHelper {
  id?: string;
  name: string;
  description: string;
  image_url: string;
  tools: string[];
  uniform: string;
  workplace: string;
  fun_fact: string;
  category_id: string | null;
  display_order: number;
  is_active: boolean;
  seo_title?: string;
  meta_description?: string;
  slug?: string;
  keywords?: string[];
  image_alt_text?: string;
}

interface CommunityHelperCategory {
  id: string;
  name: string;
}

interface CommunityHelperFormProps {
  helper?: CommunityHelper;
  onSuccess: () => void;
  onCancel: () => void;
}

const CommunityHelperForm = ({ helper, onSuccess, onCancel }: CommunityHelperFormProps) => {
  const [formData, setFormData] = useState({
    name: helper?.name || "",
    description: helper?.description || "",
    image_url: helper?.image_url || "",
    tools: helper?.tools?.join(", ") || "",
    uniform: helper?.uniform || "",
    workplace: helper?.workplace || "",
    fun_fact: helper?.fun_fact || "",
    category_id: helper?.category_id || "",
    display_order: helper?.display_order || 0,
    is_active: helper?.is_active ?? true,
    seo_title: helper?.seo_title || "",
    meta_description: helper?.meta_description || "",
    slug: helper?.slug || "",
    keywords: helper?.keywords?.join(", ") || "",
    image_alt_text: helper?.image_alt_text || "",
  });
  const [categories, setCategories] = useState<CommunityHelperCategory[]>([]);
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const { data, error } = await supabase
        .from("community_helper_categories")
        .select("id, name")
        .eq("is_active", true)
        .order("display_order");

      if (error) throw error;
      setCategories(data || []);
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
  };

  const generateSlug = (text: string) => {
    return text
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');
  };

  const handleNameChange = (name: string) => {
    setFormData({ 
      ...formData, 
      name,
      slug: formData.slug === generateSlug(formData.name) || !formData.slug ? generateSlug(name) : formData.slug
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const submitData = {
        ...formData,
        tools: formData.tools.split(",").map(tool => tool.trim()).filter(tool => tool),
        keywords: formData.keywords.split(",").map(keyword => keyword.trim()).filter(keyword => keyword),
        category_id: formData.category_id || null,
        seo_title: formData.seo_title || null,
        meta_description: formData.meta_description || null,
        slug: formData.slug || null,
        image_alt_text: formData.image_alt_text || null,
      };

      if (helper?.id) {
        const { error } = await supabase
          .from("community_helpers")
          .update(submitData)
          .eq("id", helper.id);

        if (error) throw error;
        toast({ title: "Community Helper updated successfully" });
      } else {
        const { error } = await supabase
          .from("community_helpers")
          .insert([submitData]);

        if (error) throw error;
        toast({ title: "Community Helper created successfully" });
      }

      onSuccess();
    } catch (error) {
      console.error("Error saving community helper:", error);
      toast({
        title: "Error",
        description: "Failed to save community helper",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <ScrollArea className="h-[80vh] pr-4">
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <Label htmlFor="name">Name</Label>
          <Input
            id="name"
            value={formData.name}
            onChange={(e) => handleNameChange(e.target.value)}
            required
          />
        </div>

        <div>
          <Label htmlFor="description">Description</Label>
          <RichTextEditor
            value={formData.description}
            onChange={(value) => setFormData({ ...formData, description: value })}
            placeholder="Enter community helper description"
            height="150px"
          />
        </div>

        <div>
          <Label htmlFor="image_url">Image URL</Label>
          <Input
            id="image_url"
            value={formData.image_url}
            onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
            required
          />
        </div>

        <div>
          <Label htmlFor="tools">Tools (comma-separated)</Label>
          <Input
            id="tools"
            value={formData.tools}
            onChange={(e) => setFormData({ ...formData, tools: e.target.value })}
            placeholder="stethoscope, thermometer, bandages"
          />
        </div>

        <div>
          <Label htmlFor="uniform">Uniform</Label>
          <Input
            id="uniform"
            value={formData.uniform}
            onChange={(e) => setFormData({ ...formData, uniform: e.target.value })}
            required
          />
        </div>

        <div>
          <Label htmlFor="workplace">Workplace</Label>
          <Input
            id="workplace"
            value={formData.workplace}
            onChange={(e) => setFormData({ ...formData, workplace: e.target.value })}
            required
          />
        </div>

        <div>
          <Label htmlFor="fun_fact">Fun Fact</Label>
          <Textarea
            id="fun_fact"
            value={formData.fun_fact}
            onChange={(e) => setFormData({ ...formData, fun_fact: e.target.value })}
            required
          />
        </div>

        <div>
          <Label htmlFor="category_id">Category</Label>
          <Select value={formData.category_id} onValueChange={(value) => setFormData({ ...formData, category_id: value })}>
            <SelectTrigger>
              <SelectValue placeholder="Select a category" />
            </SelectTrigger>
            <SelectContent>
              {categories.map((category) => (
                <SelectItem key={category.id} value={category.id}>
                  {category.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* SEO Fields */}
        <div className="pt-4 border-t">
          <h3 className="text-lg font-semibold mb-4">SEO Settings (Optional)</h3>
          
          <div className="space-y-4">
            <div>
              <Label htmlFor="seo_title">SEO Title</Label>
              <Input
                id="seo_title"
                value={formData.seo_title}
                onChange={(e) => setFormData({ ...formData, seo_title: e.target.value })}
                placeholder="SEO-optimized title for search engines"
                maxLength={60}
              />
              <p className="text-xs text-muted-foreground mt-1">
                {formData.seo_title.length}/60 characters
              </p>
            </div>

            <div>
              <Label htmlFor="meta_description">Meta Description</Label>
              <Textarea
                id="meta_description"
                value={formData.meta_description}
                onChange={(e) => setFormData({ ...formData, meta_description: e.target.value })}
                placeholder="Brief description for search engine results"
                maxLength={160}
                rows={3}
              />
              <p className="text-xs text-muted-foreground mt-1">
                {formData.meta_description.length}/160 characters
              </p>
            </div>

            <div>
              <Label htmlFor="slug">URL Slug</Label>
              <Input
                id="slug"
                value={formData.slug}
                onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                placeholder="url-friendly-name"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Auto-generated from name, but editable
              </p>
            </div>

            <div>
              <Label htmlFor="keywords">Keywords/Tags (comma-separated)</Label>
              <Input
                id="keywords"
                value={formData.keywords}
                onChange={(e) => setFormData({ ...formData, keywords: e.target.value })}
                placeholder="doctor, health, medical, community"
              />
            </div>

            <div>
              <Label htmlFor="image_alt_text">Image Alt Text</Label>
              <Input
                id="image_alt_text"
                value={formData.image_alt_text}
                onChange={(e) => setFormData({ ...formData, image_alt_text: e.target.value })}
                placeholder="Descriptive text for the helper image"
              />
            </div>
          </div>
        </div>

        <div>
          <Label htmlFor="display_order">Display Order</Label>
          <Input
            id="display_order"
            type="number"
            value={formData.display_order}
            onChange={(e) => setFormData({ ...formData, display_order: parseInt(e.target.value) || 0 })}
          />
        </div>

        <div className="flex items-center space-x-2">
          <input
            type="checkbox"
            id="is_active"
            checked={formData.is_active}
            onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
          />
          <Label htmlFor="is_active">Active</Label>
        </div>

        <div className="flex gap-2 pt-4">
          <Button type="submit" disabled={loading}>
            {loading ? "Saving..." : helper ? "Update Helper" : "Create Helper"}
          </Button>
          <Button type="button" variant="outline" onClick={onCancel}>
            Cancel
          </Button>
        </div>
      </form>
    </ScrollArea>
  );
};

export default CommunityHelperForm;
